import { Profile, type Credentials } from "@/app/auth/auth";
import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";

const CLIENT_SECRET = "";

export const authApi = api
  .enhanceEndpoints({ addTagTypes: ["user", "auth.user", "profiles"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      createSession: builder.mutation({
        query: ({ userId }) => ({
          url: "/auth/session",
          method: RequestMethod.POST,
          body: camelToSnake({ userId }),
        }),
        transformResponse: (response) => response?.data,
      }),
      logIn: builder.mutation({
        query: (credentials: Credentials) => ({
          url: "autho/login",
          method: RequestMethod.POST,
          body: {
            ...credentials,
            grant_type: "password",
            client_id: 2,
            client_secret: CLIENT_SECRET,
            scope: "",
          },
        }),
      }),
      signUp: builder.mutation({
        query: (body: any) => ({
          url: "/api/user-register",
          method: "POST",
          body: {
            occupation: body.occupation,
            companies: body.companies,
            company_id: body.companyId,
            email: body.email,
            name: body.name,
            password: body.password,
          },
        }),
        transformErrorResponse: (response: any) => {
          return response.data.data;
        },
      }),
      forgotPassword: builder.mutation({
        query: (body: any) => ({
          url: "/auth/forgot-password",
          method: "POST",
          body: {
            email: body.email,
          },
        }),
      }),
      resetPasword: builder.mutation({
        query: (body: { token: string; password: string }) => ({
          url: "/api/reset-password",
          method: "POST",
          body: {
            token: body.token,
            password: body.password,
          },
        }),
      }),
      changePassword: builder.mutation({
        query: ({ id, password }) => ({
          url: "/auth/change-password",
          method: RequestMethod.POST,
          body: camelToSnake({
            id,
            password,
          }),
        }),
      }),
      profiles: builder.query({
        providesTags: ["profiles"],
        query: (credentials: Credentials) => ({
          url: "/auth/profiles",
          method: RequestMethod.POST,
          body: {
            username: credentials.username,
            password: credentials.password,
          },
        }),
        transformResponse: (response: any): Array<Profile> => response?.data,
      }),
      otherProfiles: builder.query({
        providesTags: ["profiles"],
        query: () => ({
          url: "/auth/others",
          method: RequestMethod.POST,
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      session: builder.query({
        query: () => ({
          url: `/auth/user`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["auth.user"],
      }),
      getUserById: builder.query({
        providesTags: ["user"],
        query: ({ id }: any) => ({
          url: `/api/v1/user-get/${id}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => {
          return {
            id: response.data.id,
            name: response.data.name,
            email: response.data.email,
            address: response.data.address,
            occupation: response.data.occupation,
            role: response.data.role,
            status: response.data.status,
            company: response.data.company,
            createdAt: response.data.created_at,
            updatedAt: response.data.updated_at,
          };
        },
      }),
      updateUser: builder.mutation({
        invalidatesTags: ["auth.user"],
        query: ({ companyId, id }: any) => ({
          url: `/api/v1/user-update`,
          method: RequestMethod.PUT,
          body: {
            company_id: companyId,
            id: id,
          },
        }),
      }),
      deleteUser: builder.mutation({
        query: ({ id, email }: any) => ({
          url: `/api/user-delete`,
          method: RequestMethod.POST,
          body: {
            id: id,
            email: email,
          },
        }),
      }),
    }),
  });

export const {
  useLogInMutation,
  useSignUpMutation,
  useProfilesQuery,
  useLazyProfilesQuery,
  useForgotPasswordMutation,
  useResetPaswordMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useSessionQuery,
  useChangePasswordMutation,
  useCreateSessionMutation,
  useOtherProfilesQuery,
} = authApi;
